import * as microsoftTeams from '@microsoft/teams-js';
import {
  PublicClientApplication,
  createNestablePublicClientApplication,
  type IPublicClientApplication,
  type SilentRequest,
  type PopupRequest,
  type RedirectRequest,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { DrawioFile } from '../components/FileList';

/**
 * Formats parentReference.path from Graph API for breadcrumb display.
 * According to Microsoft Docs: path is percent-encoded; ignore everything before the first ":".
 * Example: "/drive/root:/Dokumente/General/trash" → "Dokumente > General > trash"
 */
function formatDisplayPath(parentPath: string | undefined | null): string {
  if (!parentPath || typeof parentPath !== 'string') return '';
  const colonIndex = parentPath.indexOf(':');
  const pathPart = colonIndex >= 0 ? parentPath.slice(colonIndex + 1) : parentPath;
  if (!pathPart.trim()) return '';
  try {
    const decoded = decodeURIComponent(pathPart.replace(/\+/g, ' '));
    const withoutLeadingSlash = decoded.replace(/^\/+/, '');
    if (!withoutLeadingSlash) return '';
    return withoutLeadingSlash.split('/').filter(Boolean).join(' > ');
  } catch {
    return pathPart.replace(/^\/+/, '').split('/').filter(Boolean).join(' > ');
  }
}

/**
 * MSAL configuration – Client ID and Tenant must match the Azure AD App Registration.
 * Set via environment variable VITE_CLIENT_ID (see .env).
 */
const CLIENT_ID = import.meta.env.VITE_CLIENT_ID as string;
const TENANT_ID = import.meta.env.VITE_TENANT_ID as string;

/**
 * MSAL base configuration (without redirectUri – set per mode).
 */
const baseMsalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    supportsNestedAppAuth: true,
  },
  cache: {
    cacheLocation: 'localStorage' as const,
    storeAuthStateInCookie: true,
  },
};

const graphScopes = [
  'https://graph.microsoft.com/Files.ReadWrite.All',
  'https://graph.microsoft.com/Sites.ReadWrite.All',
];

let msalInstance: IPublicClientApplication | null = null;
let isNestedAppAuth = false;

/**
 * Creates the MSAL instance.
 * Tries Nested App Auth (NAA) first for Teams/Outlook/Office hosts.
 * Falls back to standard PCA if NAA is not available.
 */
const getMsalInstance = async (): Promise<IPublicClientApplication> => {
  if (msalInstance) return msalInstance;

  // --- Attempt 1: Nested App Auth (works inside Teams without popup) ---
  // With NAA, no custom redirectUri must be set – the Teams broker uses brk-<clientId>://
  try {
    const naaConfig = { ...baseMsalConfig };
    msalInstance = await createNestablePublicClientApplication(naaConfig);
    isNestedAppAuth = true;
    console.log('[AUTH] MSAL Nested App Auth (NAA) initialisiert – Teams-Broker wird verwendet.');
    return msalInstance;
  } catch (naaError) {
    console.warn('[AUTH] NAA nicht verfügbar, falle auf Standard-MSAL zurück:', naaError);
  }

  // --- Attempt 2: Standard PublicClientApplication (with redirectUri for redirect flow) ---
  const pcaConfig = {
    ...baseMsalConfig,
    auth: {
      ...baseMsalConfig.auth,
      redirectUri: `${globalThis.location.origin}/index.html`,
    },
  };
  const pca = new PublicClientApplication(pcaConfig);
  await pca.initialize();
  msalInstance = pca;
  isNestedAppAuth = false;
  console.log('[AUTH] Standard MSAL PCA initialisiert.');
  return msalInstance;
};

/**
 * Gets an access token for the Microsoft Graph API.
 *
 * Strategy:
 * 1. Get Teams SSO token (audience = own app) → determine login hint.
 * 2. MSAL silent token request (with NAA, MSAL uses the Teams host broker).
 * 3. If silent fails:
 *    - NAA/Teams → acquireTokenPopup (intercepted by host broker, no real popup).
 *    - Outside Teams → acquireTokenRedirect (fallback).
 */
export const getAccessToken = async (): Promise<string> => {
  try {
    const pca = await getMsalInstance();

    // --- Step 1: Get Teams SSO token to determine login hint (UPN) ---
    let loginHint: string | undefined;
    try {
      const ssoToken = await microsoftTeams.authentication.getAuthToken();
      const payload = JSON.parse(atob(ssoToken.split('.')[1]));
      loginHint = payload.upn || payload.preferred_username;
      console.log('[AUTH] SSO login hint:', loginHint);
    } catch (error_) {
      console.warn('[AUTH] SSO token nicht verfügbar, fahre ohne Login-Hint fort:', error_);
    }

    // --- Step 2: Silent token request ---
    const accounts = pca.getAllAccounts();
    const account = accounts.length > 0
      ? accounts.find(a => a.username === loginHint) || accounts[0]
      : undefined;

    if (account) {
      try {
        const silentRequest: SilentRequest = {
          scopes: graphScopes,
          account,
        };
        const silentResult = await pca.acquireTokenSilent(silentRequest);
        console.log('[AUTH] Graph Token (silent) erhalten ✓');
        return silentResult.accessToken;
      } catch (silentError) {
        console.warn('[AUTH] Silent token acquisition fehlgeschlagen:', silentError);
      }
    }

    // --- Step 3: Interactive token request ---
    if (isNestedAppAuth) {
      // NAA: acquireTokenPopup is intercepted by Teams host broker (no real browser popup)
      console.log('[AUTH] Verwende NAA Popup (Teams-Broker) …');
      const popupRequest: PopupRequest = { scopes: graphScopes, loginHint };
      const result = await pca.acquireTokenPopup(popupRequest);
      console.log('[AUTH] Graph Token (NAA popup) erhalten ✓');
      return result.accessToken;
    }

    // Outside Teams: redirect-based flow (popups are often blocked)
    console.log('[AUTH] Verwende Redirect-Flow …');
    const redirectRequest: RedirectRequest = { scopes: graphScopes, loginHint };
    await pca.acquireTokenRedirect(redirectRequest);

    // After redirect the page reloads; handleRedirectPromise then provides the token.
    throw new Error('Redirect wird durchgeführt – Seite wird neu geladen.');
  } catch (error: any) {
    console.error('[ERROR] Fehler beim Abrufen des Graph Tokens:', error);

    if (error instanceof InteractionRequiredAuthError) {
      throw new Error('Benutzerinteraktion erforderlich. Bitte melden Sie sich erneut an.');
    }

    throw new Error(`Authentifizierung fehlgeschlagen: ${error?.message || 'Unbekannter Fehler'}`);
  }
};

/**
 * Fetches display path and driveId of a file via item request.
 * Search API does not return parentReference.path – hence separate GET.
 */
async function fetchItemDetails(
  teamId: string,
  itemId: string,
  token: string
): Promise<{ path: string; driveId?: string }> {
  try {
    const res = await fetch(
      `https://graph.microsoft.com/v1.0/groups/${teamId}/drive/items/${itemId}?$select=parentReference`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) return { path: '' };
    const item = await res.json();
    return {
      path: formatDisplayPath(item.parentReference?.path),
      driveId: item.parentReference?.driveId || undefined,
    };
  } catch {
    return { path: '' };
  }
}

/**
 * Loads all draw.io files from the team.
 */
export const getDrawioFiles = async (teamId: string): Promise<DrawioFile[]> => {
  try {
    console.log('[DEBUG] Getting access token for teamId:', teamId);
    const token = await getAccessToken();
    
    // Graph API call: search for .drawio files in the team
    const apiUrl = `https://graph.microsoft.com/v1.0/groups/${teamId}/drive/root/search(q='.drawio')?$select=id,name,webUrl,@microsoft.graph.downloadUrl,lastModifiedDateTime,createdDateTime,size,parentReference`;
    console.log('[DEBUG] Calling Graph API:', apiUrl);
    
    const searchResponse = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('[DEBUG] Graph API response status:', searchResponse.status);
    console.log('[DEBUG] Graph API response headers:', Object.fromEntries(searchResponse.headers.entries()));

    if (!searchResponse.ok) {
      const errorText = await searchResponse.text();
      console.error('[ERROR] Graph API Error Response:', {
        status: searchResponse.status,
        statusText: searchResponse.statusText,
        body: errorText
      });
      
      if (searchResponse.status === 401) {
        throw new Error('Nicht autorisiert. Bitte überprüfen Sie die App-Berechtigungen in Azure AD.');
      } else if (searchResponse.status === 403) {
        throw new Error('Zugriff verweigert. Bitte stellen Sie sicher, dass Sie Zugriff auf dieses Team haben.');
      } else if (searchResponse.status === 404) {
        throw new Error('Team nicht gefunden oder keine SharePoint-Site vorhanden.');
      } else {
        throw new Error(`Graph API Fehler: ${searchResponse.status} - ${errorText}`);
      }
    }

    const data = await searchResponse.json();
    
    console.log('[DEBUG] Graph API raw results:', data.value?.length ?? 0, 'items');
    if (data.value?.length > 0) {
      console.log('[DEBUG] First 5 raw items:', data.value.slice(0, 5).map((f: any) => ({
        name: f.name,
        id: f.id,
        path: f.parentReference?.path,
      })));
    }

    // Filter to .drawio and .xml files only (search sometimes returns other files)
    // Note: Search API does not return parentReference.path – path is loaded below
    const drawioFiles: DrawioFile[] = data.value
      .filter((file: any) => 
        file.name.endsWith('.drawio') || 
        (file.name.endsWith('.xml') && !file.name.includes('metadata'))
      )
      .map((file: any) => ({
        id: file.id,
        name: file.name,
        webUrl: file.webUrl,
        downloadUrl: file['@microsoft.graph.downloadUrl'] || file.webUrl,
        lastModifiedDateTime: file.lastModifiedDateTime,
        createdDateTime: file.createdDateTime,
        size: file.size,
        path: formatDisplayPath(file.parentReference?.path), // often empty from Search
        driveId: file.parentReference?.driveId || undefined,
      }));

    // Load paths and driveId (Search API does not return parentReference.path)
    const withPaths = await Promise.all(
      drawioFiles.map(async (file) => {
        if (file.path && file.driveId) return file;
        const details = await fetchItemDetails(teamId, file.id, token);
        return {
          ...file,
          path: file.path || details.path,
          driveId: file.driveId || details.driveId,
        };
      })
    );

    // Sort by date (newest first)
    withPaths.sort((a, b) => 
      new Date(b.lastModifiedDateTime).getTime() - new Date(a.lastModifiedDateTime).getTime()
    );

    return withPaths;
  } catch (error) {
    console.error('Fehler beim Laden der Dateien:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unbekannter Fehler beim Laden der Dateien.');
  }
};

/**
 * Gets a fresh download URL for a file.
 * Uses @microsoft.graph.downloadUrl from item metadata; if not present
 * (e.g. in some SharePoint/Teams contexts), fallback via GET /content with
 * redirect: 'manual' – the 302 response contains the pre-authenticated URL in Location.
 */
export const getFreshDownloadUrl = async (teamId: string, fileId: string): Promise<string> => {
  try {
    const token = await getAccessToken();

    // First with $select, then if needed without $select (some environments only return downloadUrl that way)
    for (const metaUrl of [
      `https://graph.microsoft.com/v1.0/groups/${teamId}/drive/items/${fileId}?$select=id,name,@microsoft.graph.downloadUrl`,
      `https://graph.microsoft.com/v1.0/groups/${teamId}/drive/items/${fileId}`,
    ]) {
      const response = await fetch(metaUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) continue;
      const data = await response.json();
      const downloadUrl = data['@microsoft.graph.downloadUrl'];
      if (downloadUrl) {
        return downloadUrl;
      }
    }

    // Fallback: /content returns 302 with pre-authenticated URL in Location header
    const contentUrl = `https://graph.microsoft.com/v1.0/groups/${teamId}/drive/items/${fileId}/content`;
    const contentResponse = await fetch(contentUrl, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      redirect: 'manual',
    });

    if (contentResponse.type === 'opaqueredirect' || contentResponse.status === 0) {
      // In some environments the redirect is not exposed – then we cannot provide a URL
      throw new Error(
        'Keine Download-URL verfügbar. Bitte nutzen Sie „Datei herunterladen“ und öffnen Sie die Datei manuell in Draw.io Desktop.'
      );
    }

    if (contentResponse.status === 302 || contentResponse.status === 301) {
      const location = contentResponse.headers.get('Location');
      if (location) {
        console.log('[GraphAPI] Download-URL über /content-Redirect ermittelt');
        return location;
      }
    }

    throw new Error(
      'Keine Download-URL verfügbar. Bitte nutzen Sie „Datei herunterladen“ und öffnen Sie die Datei manuell in Draw.io Desktop.'
    );
  } catch (error) {
    console.error('[GraphAPI] Fehler beim Abrufen der Download-URL:', error);
    throw error;
  }
};

/**
 * Fetches metadata of a single file (for hash route / "Open in browser").
 */
export const getFileMetadata = async (
  teamId: string,
  fileId: string
): Promise<DrawioFile> => {
  const token = await getAccessToken();
  const metaUrl = `https://graph.microsoft.com/v1.0/groups/${teamId}/drive/items/${fileId}?$select=id,name,webUrl,@microsoft.graph.downloadUrl,lastModifiedDateTime,createdDateTime,size,parentReference`;

  const response = await fetch(metaUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Datei nicht gefunden oder kein Zugriff: ${response.status}`);
  }

  const file: any = await response.json();
  return {
    id: file.id,
    name: file.name,
    webUrl: file.webUrl,
    downloadUrl: file['@microsoft.graph.downloadUrl'] || file.webUrl,
    lastModifiedDateTime: file.lastModifiedDateTime,
    createdDateTime: file.createdDateTime,
    size: file.size ?? 0,
    path: formatDisplayPath(file.parentReference?.path),
    driveId: file.parentReference?.driveId || undefined,
  };
};

/**
 * Loads the content of a draw.io file as XML/text via Graph API.
 * Uses downloadUrl (temporary pre-authenticated URL) or
 * falls back to the Graph API content endpoint.
 */
export const getFileContent = async (teamId: string, fileId: string): Promise<string> => {
  try {
    const token = await getAccessToken();

    // First fetch file metadata to get @microsoft.graph.downloadUrl
    const metaUrl = `https://graph.microsoft.com/v1.0/groups/${teamId}/drive/items/${fileId}?$select=id,name,@microsoft.graph.downloadUrl`;
    const metaResponse = await fetch(metaUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!metaResponse.ok) {
      throw new Error(`Fehler beim Laden der Datei-Metadaten: ${metaResponse.status}`);
    }

    const meta = await metaResponse.json();
    const downloadUrl: string | undefined = meta['@microsoft.graph.downloadUrl'];

    if (downloadUrl) {
      // downloadUrl is pre-authenticated – no Bearer token needed
      console.log('[GraphAPI] Lade Dateiinhalt über downloadUrl …');
      const contentResponse = await fetch(downloadUrl);
      if (!contentResponse.ok) {
        throw new Error(`Fehler beim Herunterladen der Datei: ${contentResponse.status}`);
      }
      return await contentResponse.text();
    }

    // Fallback: Graph API content endpoint
    console.log('[GraphAPI] Lade Dateiinhalt über /content Endpunkt …');
    const contentUrl = `https://graph.microsoft.com/v1.0/groups/${teamId}/drive/items/${fileId}/content`;
    const contentResponse = await fetch(contentUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!contentResponse.ok) {
      throw new Error(`Fehler beim Laden des Dateiinhalts: ${contentResponse.status}`);
    }

    return await contentResponse.text();
  } catch (error) {
    console.error('[GraphAPI] Fehler beim Laden des Dateiinhalts:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unbekannter Fehler beim Laden des Dateiinhalts.');
  }
};

/**
 * Saves the content (XML) of a draw.io file back to SharePoint.
 * Uses the Graph API PUT /content endpoint.
 */
export const saveFileContent = async (
  teamId: string,
  fileId: string,
  xmlContent: string
): Promise<void> => {
  try {
    const token = await getAccessToken();
    const contentUrl = `https://graph.microsoft.com/v1.0/groups/${teamId}/drive/items/${fileId}/content`;

    console.log('[GraphAPI] Speichere Dateiinhalt …');
    const response = await fetch(contentUrl, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/octet-stream',
      },
      body: xmlContent,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[GraphAPI] Fehler beim Speichern:', response.status, errorText);
      throw new Error(`Fehler beim Speichern: ${response.status} – ${errorText}`);
    }

    console.log('[GraphAPI] Datei erfolgreich gespeichert ✓');
  } catch (error) {
    console.error('[GraphAPI] Fehler beim Speichern des Dateiinhalts:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unbekannter Fehler beim Speichern der Datei.');
  }
};

/**
 * Resolves a channel ID to its display name via the Graph API.
 * This is needed on Mobile, where Teams may provide the channelId but not the channelName.
 * Requires ChannelSettings.Read.All or Channel.ReadBasic.All permission, but falls back
 * gracefully if unavailable.
 */
export const getChannelDisplayName = async (
  teamId: string,
  channelId: string
): Promise<string | null> => {
  try {
    const token = await getAccessToken();
    const url = `https://graph.microsoft.com/v1.0/teams/${teamId}/channels/${channelId}?$select=displayName`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      console.warn(`[GraphAPI] Channel-Name konnte nicht aufgelöst werden: ${response.status}`);
      return null;
    }
    const data = await response.json();
    return data.displayName || null;
  } catch (error) {
    console.warn('[GraphAPI] Fehler beim Auflösen des Channel-Namens:', error);
    return null;
  }
};

/**
 * Alternative: Loads files from a specific channel folder
 * (if search does not work or you only want to search a specific folder).
 */
export const getDrawioFilesFromFolder = async (
  teamId: string, 
  folderPath: string = 'General'
): Promise<DrawioFile[]> => {
  try {
    const token = await getAccessToken();
    
    // Graph API call: files from a specific folder
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/groups/${teamId}/drive/root:/${folderPath}:/children?$select=id,name,webUrl,@microsoft.graph.downloadUrl,lastModifiedDateTime,createdDateTime,size,parentReference`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Fehler beim Laden des Ordners: ${response.status}`);
    }

    const data = await response.json();
    
    // Filter to .drawio files only
    const drawioFiles: DrawioFile[] = data.value
      .filter((file: any) => 
        file.file && (file.name.endsWith('.drawio') || file.name.endsWith('.xml'))
      )
      .map((file: any) => ({
        id: file.id,
        name: file.name,
        webUrl: file.webUrl,
        downloadUrl: file['@microsoft.graph.downloadUrl'] || file.webUrl,
        lastModifiedDateTime: file.lastModifiedDateTime,
        createdDateTime: file.createdDateTime,
        size: file.size,
        path: formatDisplayPath(file.parentReference?.path),
        driveId: file.parentReference?.driveId || undefined,
      }));

    return drawioFiles;
  } catch (error) {
    console.error('Fehler beim Laden des Ordners:', error);
    throw error;
  }
};
